import React, { useEffect, useRef, useState } from "react";
import { parseISO, format } from "date-fns";
import { makeStyles } from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";

const safeFormatDate = (dateString, formatString) => {
  if (!dateString) return "";
  try {
    return format(parseISO(dateString), formatString);
  } catch {
    return "";
  }
};

const formatAudioTime = (seconds) => {
  if (isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const useStyles = makeStyles((theme) => ({
  audioContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "12px",
    padding: "16px 16px 28px 16px",
    maxWidth: "280px",
    minWidth: "260px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    position: "relative",
    margin: "4px 0",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    },
  },
  audioPlayer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  },
  audioPlayButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      transform: "scale(1.05)",
    },
    "& svg": {
      color: "#ffffff",
      fontSize: "20px",
    },
  },
  audioWaveform: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  audioProgress: {
    width: "100%",
    height: "4px",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: "2px",
    overflow: "hidden",
    cursor: "pointer",
  },
  audioProgressFill: {
    height: "100%",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "2px",
    transition: "width 0.1s ease",
  },
  audioDuration: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  audioWaves: {
    display: "flex",
    alignItems: "center",
    gap: "1px",
    height: "24px",
    marginBottom: "4px",
    width: "100%",
    overflow: "hidden",
  },
  audioWave: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    borderRadius: "1px",
    transition: "background-color 0.2s ease",
    minWidth: "2px",
    maxWidth: "4px",
  },
  audioWaveActive: {
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 0 2px ${theme.palette.primary.main}`,
  },
  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "black",
  },
}));

/**
 * Definido en módulo propio para que React no lo trate como tipo de componente
 * distinto en cada render del padre (evita remount y pérdida de estado del audio).
 */
const CustomAudioPlayer = ({
  audioUrl,
  messageId,
  showTimestamp,
  message,
  timestampExtra,
}) => {
  const classes = useStyles();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [waveHeights, setWaveHeights] = useState([]);

  useEffect(() => {
    const numWaves = 40;

    const createSeededRandom = (seed) => {
      let x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    const seed = messageId
      ? messageId.split("").reduce((a, b) => {
          a = (a << 5) - a + b.charCodeAt(0);
          return a & a;
        }, 0)
      : Math.random() * 10000;

    const heights = [];
    for (let i = 0; i < numWaves; i++) {
      const randomValue = createSeededRandom(seed + i);
      const height = Math.floor(4 + randomValue * 16);
      heights.push(height);
    }

    setWaveHeights(heights);
  }, [messageId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      const allAudios = document.querySelectorAll("audio");

      allAudios.forEach((a) => {
        if (a !== audio && !a.paused) {
          a.pause();
        }
      });

      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const updateProgress = () => {
      if (audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressWidth = rect.width;
    const clickProgress = (clickX / progressWidth) * 100;

    if (audio && duration > 0) {
      const newTime = (clickProgress / 100) * duration;
      audio.currentTime = newTime;
      setProgress(clickProgress);
    }
  };

  const generateWaves = () => {
    if (waveHeights.length === 0) return [];

    const waves = [];
    const numWaves = waveHeights.length;

    for (let i = 0; i < numWaves; i++) {
      const height = waveHeights[i];
      const isActive = (progress / 100) * numWaves > i;

      waves.push(
        <div
          key={i}
          className={`${classes.audioWave} ${isActive ? classes.audioWaveActive : ""}`}
          style={{ height: `${height}px` }}
        />
      );
    }
    return waves;
  };

  return (
    <div className={classes.audioContainer}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <div className={classes.audioPlayer}>
        <button type="button" className={classes.audioPlayButton} onClick={togglePlay}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </button>

        <div className={classes.audioWaveform}>
          <div className={classes.audioWaves}>{generateWaves()}</div>

          <div className={classes.audioProgress} onClick={handleProgressClick}>
            <div className={classes.audioProgressFill} style={{ width: `${progress}%` }} />
          </div>

          <div className={classes.audioDuration}>
            {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
          </div>
        </div>
      </div>

      {showTimestamp && (
        <span
          className={classes.timestamp}
          style={{
            position: "absolute",
            bottom: "8px",
            right: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "2px 4px",
            borderRadius: "4px",
            fontSize: "11px",
          }}
        >
          {safeFormatDate(message?.createdAt, "HH:mm")}
          {timestampExtra}
        </span>
      )}
    </div>
  );
};

export default CustomAudioPlayer;
